import { useState } from 'react';
import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { CopyRegular, LockClosedRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { UserPicker } from '../features/users/UserPicker';
import type { DirectoryUserDto, LapsResult, ManagedDeviceDto } from '../api/types';
import './devices.css';

export function LapsPage() {
  const api = useApi();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const policy = policies.data?.find((p) => p.pageKey === 'devices.laps');
  const ticketMode = policy?.ticketMode ?? 'None';
  const approvalRequired = policy?.approvalMode === 'Required';

  const [selected, setSelected] = useState<DirectoryUserDto | undefined>();
  const [devices, setDevices] = useState<ManagedDeviceDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [target, setTarget] = useState<ManagedDeviceDto | null>(null);
  const [justification, setJustification] = useState('');
  const [ticket, setTicket] = useState('');
  const [busy, setBusy] = useState(false);
  const [dlgError, setDlgError] = useState<string | null>(null);
  const [result, setResult] = useState<LapsResult | null>(null);
  const [pending, setPending] = useState(false);
  const [copied, setCopied] = useState(false);

  async function pick(u: DirectoryUserDto | undefined) {
    setSelected(u);
    setDevices(null);
    setListError(null);
    if (!u) return;
    setLoading(true);
    try {
      setDevices(await api.getUserDevices(u.id));
    } catch (e) {
      setListError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  const lapsDevices = (devices ?? []).filter(
    (d) => (d.operatingSystem ?? '').toLowerCase().includes('windows') && d.azureAdDeviceId,
  );

  function open(d: ManagedDeviceDto) {
    setTarget(d);
    setJustification('');
    setTicket('');
    setDlgError(null);
    setResult(null);
    setPending(false);
    setCopied(false);
  }

  async function submit() {
    if (!target) return;
    if (!justification.trim()) {
      setDlgError('Eine Begründung ist erforderlich.');
      return;
    }
    if (ticketMode === 'Required' && !ticket.trim()) {
      setDlgError('Ticketnummer ist erforderlich.');
      return;
    }
    setBusy(true);
    setDlgError(null);
    try {
      const res = await api.requestLaps({
        azureAdDeviceId: target.azureAdDeviceId!,
        deviceName: target.deviceName,
        justification: justification.trim(),
        ticketNumber: ticket.trim() || undefined,
      });
      if (res.status === 'PendingApproval') setPending(true);
      else if (res.result) setResult(res.result);
    } catch (e) {
      setDlgError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function copySecret() {
    if (!result) return;
    navigator.clipboard
      .writeText(result.secret)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  }

  return (
    <div>
      <PageHeader
        title="LAPS"
        description="Lokales Admin-Passwort der Windows-Geräte eines Benutzers einsehen (Windows-LAPS)."
      />
      <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Hochsensibler Zugriff</MessageBarTitle>
            Jeder LAPS-Abruf wird vollständig auditiert; eine Begründung ist Pflicht.
            {approvalRequired ? ' Diese Aktion ist freigabepflichtig — das Passwort erscheint nach der Freigabe unter „Meine Anfragen".' : ''}
          </MessageBarBody>
        </MessageBar>

        <Field label="Benutzer">
          <UserPicker onSelect={pick} />
        </Field>

        {listError ? (
          <MessageBar intent="error">
            <MessageBarBody>{listError}</MessageBarBody>
          </MessageBar>
        ) : null}
        {loading ? <Spinner size="tiny" label="Lade Geräte…" /> : null}

        {selected && devices && lapsDevices.length === 0 ? (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Keine Windows-Geräte mit Entra-Registrierung gefunden (LAPS setzt Windows-LAPS in Entra voraus).
          </Caption1>
        ) : null}

        {lapsDevices.map((d) => (
          <div className="dev-laps-row" key={d.id}>
            <div>
              <Text weight="semibold">{d.deviceName}</Text>
              <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                {d.operatingSystem} {d.osVersion} {d.model ? `· ${d.model}` : ''}
              </Caption1>
            </div>
            <Button appearance="primary" icon={<LockClosedRegular />} onClick={() => open(d)}>
              Passwort abrufen
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={target !== null} onOpenChange={(_, d) => (d.open ? undefined : setTarget(null))}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>LAPS-Passwort{target ? ` · ${target.deviceName}` : ''}</DialogTitle>
            <DialogContent>
              {result ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Konto: {result.accountName}</Caption1>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <span className="dev-secret pii">{result.secret}</span>
                    <Button size="small" appearance="subtle" icon={<CopyRegular />} onClick={copySecret}>
                      {copied ? 'Kopiert' : 'Kopieren'}
                    </Button>
                  </div>
                  {result.backupUtc ? (
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      Gesichert am {new Date(result.backupUtc).toLocaleString('de-DE')}
                    </Caption1>
                  ) : null}
                </div>
              ) : pending ? (
                <MessageBar intent="info">
                  <MessageBarBody>
                    <MessageBarTitle>Antrag eingereicht</MessageBarTitle>
                    Der Abruf ist freigabepflichtig. Nach der Freigabe erscheint das Passwort unter „Meine Anfragen".
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                  {dlgError ? (
                    <MessageBar intent="error">
                      <MessageBarBody>{dlgError}</MessageBarBody>
                    </MessageBar>
                  ) : null}
                  <Field label="Begründung" required hint="Wird auditiert.">
                    <Textarea
                      value={justification}
                      onChange={(_, v) => setJustification(v.value)}
                      rows={3}
                      placeholder="z. B. Vor-Ort-Support an Gerät, Ticket REQ…"
                    />
                  </Field>
                  {ticketMode !== 'None' ? (
                    <Field label="Ticketnummer" required={ticketMode === 'Required'}>
                      <Input value={ticket} onChange={(_, v) => setTicket(v.value)} placeholder="z. B. REQ0012345" />
                    </Field>
                  ) : null}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setTarget(null)} disabled={busy}>
                {result || pending ? 'Schließen' : 'Abbrechen'}
              </Button>
              {!result && !pending ? (
                <Button appearance="primary" onClick={submit} disabled={busy}>
                  {busy ? 'Bitte warten…' : approvalRequired ? 'Antrag stellen' : 'Passwort abrufen'}
                </Button>
              ) : null}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
